import React, { useState } from 'react';
import { CheckIcon, ClipboardDocumentIcon } from '@heroicons/react/24/outline';
import { Trans, useTranslation } from 'react-i18next';
import { DocFieldGroup, ECOM_FIELDS, POS_FIELDS, Requirement, SERVICES_BY_REGION } from './apiDocsFields';

/*
 * API documentation for resellers. The text is translated (apiDocs.* in the
 * locale files); field names, code examples and HTTP status names stay as they
 * are, since they are part of the API itself.
 */

// Always production, whatever environment the dashboard runs in: these docs are
// for resellers. Every onboarding domain serves the API under /api; this one
// matches REACT_APP_API_URL in env.production.example.
export const DEFAULT_BASE_URL = 'https://onboarding.ccs.ag/api';

// Matches API_RATE_LIMIT in the backend (api-key-rate-limit.guard.ts)
const RATE_LIMIT_PER_MINUTE = 30;

const ecomExample = (baseUrl: string) => `curl -X POST "${baseUrl}/v1/precheck?language=en" \\
  -H "X-API-Key: $API_KEY" \\
  -H "Content-Type: application/json" \\
  -d '{
    "externalReference": "crm-4711",
    "notifyMerchant": true,
    "reg": "eu",
    "srv": ["creditCard"],
    "genInfo": {
      "genComp": "Acme Shop Ltd",
      "genAddr": "1 High Street, London, UK",
      "genDirName": "Jane Doe",
      "genDirEmail": "jane.doe@acme.example",
      "genDirAddr": "2 Low Street, London, UK",
      "genTaxID": "GB123456789",
      "genDesc": "Online shop for garden tools",
      "genPrice": "10-250 EUR per order",
      "genUrl": "https://shop.acme.example",
      "genFullName": "John Doe",
      "genEmail": "john.doe@acme.example"
    },
    "ccpInfo": {
      "ccpFulfill": "no",
      "ccpCallCtr": "no",
      "ccpLic": "no",
      "ccpBankAcq": "None",
      "ccpAvgTxVal": "45",
      "ccpCurrMonthVol": "20000",
      "ccpExpMonthVol": "35000"
    }
  }'`;

const posExample = (baseUrl: string) => `curl -X POST "${baseUrl}/v1/precheck-pos?language=de" \\
  -H "X-API-Key: $API_KEY" \\
  -H "Content-Type: application/json" \\
  -d '{
    "externalReference": "crm-4712",
    "notifyMerchant": false,
    "company": "Acme Café GmbH",
    "taxId": "DE123456789",
    "address": "Hauptstraße 1, 10115 Berlin, Germany",
    "director": "Jane Doe",
    "contactPerson": "John Doe",
    "email": "john.doe@acme.example",
    "telephone": "+49301234567",
    "currentProvider": "N/A",
    "turnover": "15000",
    "receiptSize": "12",
    "bankStatementReference": "ACME CAFE",
    "terminals": "yes",
    "checkoutConnection": "no",
    "cashCut": "standard",
    "tip": "without-signature",
    "cards": ["Visa/Mastercard", "Girocard/EC cash"],
    "additionalServices": ["DCC"]
  }'`;

const responseExample = (type: string, reference: string) => `{
  "id": 1234,
  "type": "${type}",
  "externalReference": "${reference}",
  "createdAt": "2026-10-08T14:30:00.000Z",
  "test": false
}`;

// The API itself answers in English
const ERROR_EXAMPLE = `{
  "statusCode": 400,
  "error": "Bad Request",
  "message": [
    "genInfo.genEmail must be an email",
    "Missing required fields for the selected services: ccpInfo.ccpBankAcq"
  ]
}`;

// [status, key below apiDocs.errors.status]
const STATUS_CODES: [string, string][] = [
  ['201 Created', 'created'],
  ['200 OK', 'ok'],
  ['400 Bad Request', 'badRequest'],
  ['401 Unauthorized', 'unauthorized'],
  ['403 Forbidden', 'forbidden'],
  ['409 Conflict', 'conflict'],
  ['429 Too Many Requests', 'tooManyRequests'],
  ['5xx', 'serverError'],
];

type TabKey = 'overview' | 'ecommerce' | 'pos' | 'errors';

const TABS: TabKey[] = ['overview', 'ecommerce', 'pos', 'errors'];

// children are optional so it can also serve as a <Trans> component
const Code: React.FC<{ children?: React.ReactNode }> = ({ children }) => (
  <code className="rounded bg-slate-100 px-1.5 py-0.5 font-mono text-[0.85em] text-slate-800">{children}</code>
);

const CODE_COMPONENTS = { code: <Code /> };

const CodeBlock: React.FC<{ code: string }> = ({ code }) => {
  const { t } = useTranslation();
  const [copyState, setCopyState] = useState<'idle' | 'copied' | 'failed'>('idle');

  // The clipboard API is missing outside secure contexts and may be denied
  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(code);
      setCopyState('copied');
    } catch {
      setCopyState('failed');
    }
    setTimeout(() => setCopyState('idle'), 2000);
  };

  return (
    <div className="relative">
      <button
        type="button"
        onClick={handleCopy}
        className="absolute right-3 top-3 inline-flex items-center gap-1 rounded-md bg-slate-700 px-2 py-1 text-xs font-medium text-slate-100 hover:bg-slate-600"
      >
        {copyState === 'copied' ? <CheckIcon className="size-4" /> : <ClipboardDocumentIcon className="size-4" />}
        {copyState === 'copied'
          ? t('apiDocs.copied')
          : copyState === 'failed'
            ? t('apiDocs.copyFailed')
            : t('apiDocs.copy')}
      </button>
      <pre className="overflow-x-auto rounded-xl bg-slate-900 p-4 pr-24 text-sm leading-relaxed text-slate-100">
        <code>{code}</code>
      </pre>
    </div>
  );
};

const Endpoint: React.FC<{ path: string; description: string }> = ({ path, description }) => (
  <div className="flex flex-wrap items-center gap-3 rounded-xl border border-slate-200 bg-slate-50 px-4 py-3">
    <span className="rounded-md bg-green-600 px-2 py-0.5 text-xs font-bold text-white">POST</span>
    <code className="font-mono text-sm text-slate-900">{path}</code>
    <span className="text-sm text-slate-600">{description}</span>
  </div>
);

const SubHeading: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <h3 className="text-lg font-semibold text-slate-900">{children}</h3>
);

const FieldTable: React.FC<{ group: DocFieldGroup }> = ({ group }) => {
  const { t } = useTranslation();

  const requirement = (required: Requirement) =>
    typeof required === 'string'
      ? t(`apiDocs.required.${required}`)
      : t('apiDocs.required.ifService', { service: required.service });

  // service groups are titled with their JSON property, e.g. "ccpInfo — …"
  const title = ['root', 'body'].includes(group.id)
    ? t(`apiDocs.groups.${group.id}`)
    : `${group.id} — ${t(`apiDocs.groups.${group.id}`)}`;

  return (
    <div className="space-y-2">
      <h4 className="font-semibold text-slate-800">{title}</h4>
      <div className="overflow-x-auto rounded-xl border border-slate-200">
        <table className="w-full table-fixed text-left text-sm">
          <thead className="bg-slate-50 text-slate-700">
            <tr>
              <th className="w-1/5 px-3 py-2 font-medium">{t('apiDocs.table.field')}</th>
              <th className="w-36 px-3 py-2 font-medium">{t('apiDocs.table.type')}</th>
              <th className="w-1/5 px-3 py-2 font-medium">{t('apiDocs.table.required')}</th>
              <th className="px-3 py-2 font-medium">{t('apiDocs.table.description')}</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-200">
            {group.fields.map((field) => (
              <tr key={field.name} className="align-top">
                <td className="break-words px-3 py-2 font-mono text-xs text-slate-900">{field.name}</td>
                <td className="px-3 py-2 text-slate-600">{field.type}</td>
                <td className="px-3 py-2 text-slate-600">{requirement(field.required)}</td>
                <td className="px-3 py-2 text-slate-700">
                  {t(`apiDocs.fields.${field.key ?? field.name}`)}
                  {field.values && (
                    <div className="mt-1 flex flex-wrap gap-1">
                      {field.values.map((value) => (
                        <Code key={value}>{value}</Code>
                      ))}
                    </div>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
};

const Overview: React.FC<{ baseUrl: string }> = ({ baseUrl }) => {
  const { t } = useTranslation();

  return (
    <div className="space-y-6 text-sm leading-relaxed text-slate-700">
      <p>{t('apiDocs.overview.intro')}</p>

      <div className="space-y-2">
        <SubHeading>{t('apiDocs.overview.baseUrl')}</SubHeading>
        <CodeBlock code={baseUrl} />
      </div>

      <div className="space-y-2">
        <SubHeading>{t('apiDocs.overview.authentication')}</SubHeading>
        <p>
          <Trans i18nKey="apiDocs.overview.authHeader" components={CODE_COMPONENTS} />
        </p>
        <CodeBlock code="X-API-Key: onb_..." />
        <p>{t('apiDocs.overview.authKeys')}</p>
      </div>

      <div className="space-y-2">
        <SubHeading>{t('apiDocs.overview.domain')}</SubHeading>
        <p>{t('apiDocs.overview.domainText')}</p>
      </div>

      <div className="space-y-2">
        <SubHeading>{t('apiDocs.overview.emails')}</SubHeading>
        <p>
          <Trans i18nKey="apiDocs.overview.emailsText" components={CODE_COMPONENTS} />
        </p>
      </div>

      <div className="space-y-2">
        <SubHeading>{t('apiDocs.overview.retries')}</SubHeading>
        <p>
          <Trans i18nKey="apiDocs.overview.retriesText" components={CODE_COMPONENTS} />
        </p>
      </div>

      <div className="space-y-2">
        <SubHeading>{t('apiDocs.overview.testing')}</SubHeading>
        <p>
          <Trans i18nKey="apiDocs.overview.testingText" components={CODE_COMPONENTS} />
        </p>
      </div>

      <div className="space-y-2">
        <SubHeading>{t('apiDocs.overview.validation')}</SubHeading>
        <p>{t('apiDocs.overview.validationText')}</p>
      </div>
    </div>
  );
};

const RegionServices: React.FC = () => {
  const { t } = useTranslation();

  return (
    <div className="overflow-x-auto rounded-xl border border-slate-200">
      <table className="w-full table-fixed text-left text-sm">
        <thead className="bg-slate-50 text-slate-700">
          <tr>
            <th className="w-1/5 px-3 py-2 font-medium">reg</th>
            <th className="px-3 py-2 font-medium">{t('apiDocs.endpoint.regionServices')}</th>
          </tr>
        </thead>
        <tbody className="divide-y divide-slate-200">
          {Object.entries(SERVICES_BY_REGION).map(([region, services]) => (
            <tr key={region} className="align-top">
              <td className="px-3 py-2 font-mono text-xs text-slate-900">{region}</td>
              <td className="px-3 py-2">
                <div className="flex flex-wrap gap-1">
                  {services.map((service) => (
                    <Code key={service}>{service}</Code>
                  ))}
                </div>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
};

const SubmissionDocs: React.FC<{
  path: string;
  description: string;
  example: string;
  response: string;
  groups: DocFieldGroup[];
  intro?: React.ReactNode;
}> = ({ path, description, example, response, groups, intro }) => {
  const { t } = useTranslation();

  return (
    <div className="space-y-6 text-sm leading-relaxed text-slate-700">
      <Endpoint path={path} description={description} />
      <p>
        <Trans i18nKey="apiDocs.endpoint.body" components={CODE_COMPONENTS} />
      </p>
      {intro}

      <div className="space-y-2">
        <SubHeading>{t('apiDocs.endpoint.exampleRequest')}</SubHeading>
        <CodeBlock code={example} />
      </div>

      <div className="space-y-2">
        <SubHeading>{t('apiDocs.endpoint.response')}</SubHeading>
        <p>
          <Trans i18nKey="apiDocs.endpoint.responseText" components={CODE_COMPONENTS} />
        </p>
        <CodeBlock code={response} />
      </div>

      <div className="space-y-4">
        <SubHeading>{t('apiDocs.endpoint.fields')}</SubHeading>
        {groups.map((group) => (
          <FieldTable key={group.id} group={group} />
        ))}
      </div>
    </div>
  );
};

const ErrorsAndLimits: React.FC = () => {
  const { t } = useTranslation();

  return (
    <div className="space-y-6 text-sm leading-relaxed text-slate-700">
      <div className="space-y-2">
        <SubHeading>{t('apiDocs.errors.statusCodes')}</SubHeading>
        <div className="overflow-x-auto rounded-xl border border-slate-200">
          <table className="w-full text-left text-sm">
            <tbody className="divide-y divide-slate-200">
              {STATUS_CODES.map(([status, key]) => (
                <tr key={status} className="align-top">
                  <td className="whitespace-nowrap px-3 py-2 font-mono text-xs text-slate-900">{status}</td>
                  <td className="px-3 py-2">{t(`apiDocs.errors.status.${key}`)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      <div className="space-y-2">
        <SubHeading>{t('apiDocs.errors.errorBody')}</SubHeading>
        <p>
          <Trans i18nKey="apiDocs.errors.errorBodyText" components={CODE_COMPONENTS} />
        </p>
        <CodeBlock code={ERROR_EXAMPLE} />
      </div>

      <div className="space-y-2">
        <SubHeading>{t('apiDocs.errors.rateLimit')}</SubHeading>
        <p>
          <Trans
            i18nKey="apiDocs.errors.rateLimitText"
            values={{ limit: RATE_LIMIT_PER_MINUTE }}
            components={CODE_COMPONENTS}
          />
        </p>
      </div>
    </div>
  );
};

const ApiDocs: React.FC<{ baseUrl: string }> = ({ baseUrl }) => {
  const { t } = useTranslation();
  const [tab, setTab] = useState<TabKey>('overview');

  return (
    <div className="overflow-hidden rounded-xl border border-slate-200 bg-white shadow-sm">
      <div className="flex flex-wrap gap-1 border-b border-slate-200 bg-slate-50 px-4 pt-3">
        {TABS.map((key) => (
          <button
            key={key}
            type="button"
            onClick={() => setTab(key)}
            className={`-mb-px rounded-t-lg border px-4 py-2 text-sm font-medium transition-colors ${tab === key
                ? 'border-slate-200 border-b-white bg-white text-blue-700'
                : 'border-transparent text-slate-600 hover:text-slate-900'
              }`}
          >
            {t(`apiDocs.tabs.${key}`)}
          </button>
        ))}
      </div>

      <div className="p-6">
        {tab === 'overview' && <Overview baseUrl={baseUrl} />}
        {tab === 'ecommerce' && (
          <SubmissionDocs
            path="/v1/precheck"
            description={t('apiDocs.endpoint.ecommerce')}
            example={ecomExample(baseUrl)}
            response={responseExample('ecommerce', 'crm-4711')}
            groups={ECOM_FIELDS}
            intro={
              <>
                <p>
                  <Trans i18nKey="apiDocs.endpoint.ecommerceIntro" components={CODE_COMPONENTS} />
                </p>
                <RegionServices />
              </>
            }
          />
        )}
        {tab === 'pos' && (
          <SubmissionDocs
            path="/v1/precheck-pos"
            description={t('apiDocs.endpoint.pos')}
            example={posExample(baseUrl)}
            response={responseExample('pos', 'crm-4712')}
            groups={POS_FIELDS}
          />
        )}
        {tab === 'errors' && <ErrorsAndLimits />}
      </div>
    </div>
  );
};

export default ApiDocs;
